import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithPopup,
  OAuthProvider,
  signOut,
  onIdTokenChanged,
  User,
  Auth
} from 'firebase/auth';
import { environment } from '../../../../environments/environment';
import { AuthResponse, AuthRequest, AccountSummary } from '../models/auth.models';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  // Inicialización de Firebase segun instrucción 3
  private readonly app = initializeApp(environment.firebase);
  private readonly auth: Auth = getAuth(this.app);

  private readonly apiUrl = `${environment.authApiBaseUrl}/api/v1/auth`;
  private readonly commonHeaders = { 'x-requested-by': environment.xRequestedBy };

  async signInWithMicrosoft(): Promise<string> {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      tenant: '5daabe65-ff53-4336-a448-2f61ecf0b059',
      prompt: 'select_account'
    });
    provider.addScope('mail.read');

    const result = await signInWithPopup(this.auth, provider);
    return await result.user.getIdToken();
  }

  async login(firebaseToken: string): Promise<AuthResponse> {
    const body: AuthRequest = {
      firebaseToken,
      productCode: environment.productCode
    };

    return await firstValueFrom(
      this.http.post<AuthResponse>(`${this.apiUrl}/session/login`, body, {
        headers: this.commonHeaders,
        withCredentials: true
      })
    );
  }

  async logout(): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(`${this.apiUrl}/logout`, {}, {
          headers: this.commonHeaders,
          withCredentials: true
        })
      );
    } finally {
      await signOut(this.auth);
    }
  }

  async getMe(): Promise<AccountSummary> {
    const response = await firstValueFrom(
      this.http.get<AuthResponse>(`${this.apiUrl}/me`, {
        headers: this.commonHeaders,
        withCredentials: true
      })
    );
    return response.account;
  }

  async refresh(firebaseToken: string): Promise<void> {
    const body: AuthRequest = {
      firebaseToken,
      productCode: environment.productCode
    };

    await firstValueFrom(
      this.http.post(`${this.apiUrl}/refresh`, body, {
        headers: this.commonHeaders,
        withCredentials: true
      })
    );
  }

  onIdTokenChanged(callback: (token: string | null) => void) {
    return onIdTokenChanged(this.auth, async (user: User | null) => {
      if (user) {
        const token = await user.getIdToken();
        callback(token);
      } else {
        callback(null);
      }
    });
  }
}
